/**
 * WHERE A RESERVATION LANDS (owner, 2026-08-27).
 *
 * Holding a spot opens the conversation it created, exactly as knocking opens
 * the one a knock creates — and it is the SAME landing from every road in: the
 * knock dialog's queues, a typed handle nobody has queued at, and the waiting
 * room a signed-in reader meets at `/@handle`. A reserve that left somebody
 * looking at the row they had just pressed was the product doing the work and
 * then not showing it. One copy of the rule, because three copies is how three
 * entry points come to behave differently.
 *
 * THE ROW IS SEEDED RATHER THAN AWAITED. The list is a read of
 * `GET /waiting/mine`, and selecting a conversation the browser has not been
 * told about yet is a blank pane for one round trip. What the server is about
 * to answer is exactly what was just posted, so it is written into the cache
 * and then re-read — the invalidation immediately after is what makes any
 * trimming the server did right.
 */
import { useQueryClient } from "@tanstack/react-query";
import type { MyWaiting } from "../../lib/api";
import { COMPOSE_ID, spotIdFor } from "../../lib/conversations";
import { qk } from "../../lib/queries";
import { useShellUi } from "../shell/state";

/**
 * No position (ruling R-0916-3): a queue number is a count said another way,
 * and the sender is the one reader who may not learn it.
 */
export type Land = (handle: string, line: string) => void;

export function useLandInSpot(): Land {
  const ui = useShellUi();
  const client = useQueryClient();
  return (handle, line) => {
    // An empty line is a spot that is ALREADY held being opened — there is
    // nothing new to write down, and writing it down would replace the
    // registrant's own sentence with a blank one.
    if (line) {
      client.setQueryData(qk.myWaiting(), (rows: MyWaiting[] | undefined) => [
        ...(rows ?? []).filter((r) => r.handle.toLowerCase() !== handle.toLowerCase()),
        // The whole row the server is about to answer with, `state` included:
        // a registration that just landed is active by definition, and a seed
        // missing the field would read as neither held nor stopped.
        {
          handle,
          line,
          state: "active" as const,
          created_at: new Date().toISOString(),
          doorOpen: false,
          knocked: false,
        },
      ]);
    }
    // The reader's own list gained a row, and it is read by surfaces on screen
    // right now, so it is re-read rather than left to a reload.
    void client.invalidateQueries({ queryKey: qk.myWaiting() });
    ui.closeSheet();
    /**
     * A SPOT REGISTERED FROM ITS OWN THREAD DOES NOT MOVE.
     *
     * A compose thread opened at a handle with no door is one of the three
     * roads in, and once the registration lands the list draws the spot under
     * THAT thread's id (`use-conversations.ts`) — so the row transforms where
     * it stands. Selecting `spot:handle` here would point at a row nobody
     * drew and blank the pane the reader is looking at.
     */
    const inThread = ui.compose?.handle.toLowerCase() === handle.replace(/^@/, "").toLowerCase();
    ui.setActive(inThread ? COMPOSE_ID : spotIdFor(handle));
  };
}
